import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
	Container,
	Paper,
	Grid,
	Table,
	TableBody,
	TableCell as TableCellMui,
	TableContainer,
	TableHead,
	TableRow,
	LinearProgress,
	Typography,
	InputAdornment,
	TextField,
	Switch,
	FormControlLabel,
	Chip,
	Link
} from '@mui/material';
import {
	useAllQuicksightDashboard,
	useNewQuicksightDashboard,
	useUpdateQuicksightDashboard,
	useDonors
} from 'shared/apollo';
import { DetailPopup, ConfirmationDialog } from 'shared/components';
import { Search as SearchIcon, Save as SaveIcon, Done as DoneIcon } from '@mui/icons-material';
import _ from 'lodash';
import Row from './row';
import QsForm from './qsForm';

export default function QuicksightAdmin() {
	const { data: rows, isLoading, refresh } = useAllQuicksightDashboard();
	const { run: runNew, isLoading: loadingNew } = useNewQuicksightDashboard();
	const { run: runEdit, isLoading: loadingEdit } = useUpdateQuicksightDashboard();
	const { data: donors } = useDonors();
	const [ open, setOpen ] = useState(false);
	const [ selectedRow, setSelectedRow ] = useState();
	const [ search, setSearch ] = useState('');
	const [isLoadingDashboard, setIsLoadingDashboards] = useState(false);
	const [dashboards, setDashboards] = useState([]);
	const [list, setList] = useState([]);
	const [onlyShared, setOnlyShared] = useState(false);
	const [showSaveChip, setShowSaveChip] = useState(false);
	const [showInstructions, setShowInstructions] = useState();

	useEffect(() => {
		pullQuickSightDashboards();
	}, []);

	useEffect(() => {
		if (dashboards && rows && dashboards.length > 0 && rows.length > 0) {
			const dashboardList = dashboards.map(({name, id: awsId, created}) => {
				const { accountList, dbId, information } = rows.filter(row => row.awsId === awsId)?.[0] ?? {}; 
				return {
					accountList: JSON.parse(accountList || '[]'),
					awsId,
					dbId,	 		
					information: information || '',
					name,
					created
				};
			});
			setList(dashboardList);
		}
	}, [dashboards, rows]);

  const pullQuickSightDashboards = async () => {
    setIsLoadingDashboards(true);
		const response = await axios.get('v4/quicksight');
		setIsLoadingDashboards(false);
		const boards = response.data.dashboards;
		setDashboards(boards);
		setList(boards.map(({name, id: awsId, created}) => {
			return {
				name,
				created,
				awsId,
				dbId: undefined,	 		
				information: '',
				accountList: [],
			};
		}));
	};
	
	const onClickEdit = (row) => {
		setSelectedRow(row);
		setShowInstructions();
		setOpen(true);
	};

	const handleCloseModal = () => {
		setOpen(false);
	};

	const filterData = () => {
		let data = (search) ?
			list.filter(row => {
				return (row.name.toUpperCase().indexOf(search.toUpperCase()) > -1) ||
				(row.information.toUpperCase().indexOf(search.toUpperCase()) > -1);
			})
			: list;
		if (onlyShared) {
			return list.filter(row => row.accountList.length > 0);
		}
		return data;
	};

	const handleSave = (values, resetForm) => {
		setOpen(false);
		if (values.id) {
			runEdit(values, () => {
				refresh();
				resetForm({values: ''});
			});
		} else {
			runNew(values, () => {
				refresh();
				resetForm({values: ''});
			});
		}
		setShowSaveChip(true);
		setTimeout(() => {
			setShowSaveChip(false);
		}, 1500);
	};

	const handleChangeOnlyShared = (event) => {
    setOnlyShared(event.target.checked);
  };

	const filteredList = filterData();

	return (
		<Container maxWidth={false} style={{marginTop: '10px'}}>
			<Grid container spacing={2} justify="center" alignItems="center">
				<Grid container direction="row" justify="space-between" alignItems="center">
					<Grid item xs={6}>
						<TextField
							label="Search"
							value={search}
							onChange={e => setSearch(e.target.value)}
							fullWidth
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								)
							}}
						/>
					</Grid>
					<Grid item xs={6} style={{justifyContent: 'flex-end', textAlign: 'right'}}>
						<FormControlLabel
							control={<Switch checked={onlyShared} onChange={handleChangeOnlyShared}/>}
							label="Show only shared"
						/>
					</Grid>
				</Grid>
				{ (isLoading || loadingNew || loadingEdit || isLoadingDashboard) && (<Grid
					item
					xs={12}
				><LinearProgress variant="query"/></Grid>)}
				{ showSaveChip && (
					<Grid item xs={12}>
						<Chip
							label="Dashboard successfully updated"	
							deleteIcon={<DoneIcon />}
							variant="outlined"
							color="secondary"
							style={{float: 'right'}}
							clickable
							onDelete={() => setShowSaveChip(false)}
						/>
					</Grid>
				)}
				<Grid container spacing={2} justify="center" alignItems="center" style={{marginTop: '5px'}}>
					{ !isLoading && list.length > 0 && (
					<Grid item xs={12}>
							<TableContainer component={Paper}>
								<Table aria-label="collapsible table" size="small">
									<TableHead>
										<TableRow>
											<TableCell align="left">Name</TableCell>
											<TableCell align="left">Description</TableCell>
											<TableCell align="center">Created</TableCell>
											<TableCell align="center">Accounts</TableCell>
											<TableCell align="center">Account List</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>{filteredList.map((row) => <Row
										key={row.dbId}
										row={row}
										onClickEdit={onClickEdit}
									/>)}</TableBody>
								</Table>
							</TableContainer>
					</Grid>)}
					{ !isLoading && list.length > 0 && (
						<Grid item xs={12} style={{paddingBottom: '20px'}}>
							<Link
								style={{color: '#0091ae', cursor: 'pointer', }}
								onClick={() => setShowInstructions(true)}
							>Can't find your dashboard? how to add a new one</Link>
						</Grid>
					)}
					{ !isLoading && !isLoadingDashboard && dashboards.length === 0 && (
						<Grid item xs={12}>
							<Typography component="subtitle2" variant="subtitle2" noWrap>{'There are no dashbaords yet...'}</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>
			{ open && (
				<DetailPopup open={open} onClose={handleCloseModal} insideCard={false} width={'450px'}>
					<Container maxWidth={false}>
						{donors && donors.length > 0 && (<QsForm
							donors={donors}
							handleSave={handleSave}
							selectedRow={selectedRow}
							dashboards={dashboards}
						/>)}
					</Container>
				</DetailPopup>
			)}
			{ showInstructions && (
				<DetailPopup
					open={showInstructions}
					onClose={() => setShowInstructions()}
					insideCard={false}
					width={'450px'}
					title="FAQ: Adding a new dashboard to quicksight"
				>
					<Container maxWidth={false}>
						<h3 style={{color: '#0091ae'}}>How do I add a new dashboard?:</h3>
						<p>To add a new dashboard you must:</p>
						<ul>
							<li>Log in to AWS with your authorized quicksight user</li>
							<li>Browser services and navigate to quicksight</li>
							<li>Create an analysis using the designed tool</li>
							<li>Deploy / Share the analysis as a dashboard</li>
						</ul>
						<p>For more information please contact the TI administrator.</p>
					</Container>
				</DetailPopup>
			)}
		</Container>
	);
}

const TableCell = styled(TableCellMui)`
  width: 20%;
`;